import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api.js';

// ── Queries ───────────────────────────────────────────────────────────────────

export function useIntercompanyPeriods({ from, to } = {}) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString() ? `?${params.toString()}` : '';

  return useQuery({
    queryKey: ['intercompany', 'periods', { from, to }],
    queryFn: () => api.get(`/intercompany/periods${qs}`),
    staleTime: 30_000,
  });
}

export function useIntercompanyPeriod(yyyymm) {
  return useQuery({
    queryKey: ['intercompany', 'periods', yyyymm],
    queryFn: () => api.get(`/intercompany/periods/${yyyymm}`),
    staleTime: 30_000,
    enabled: !!yyyymm,
  });
}

// ── Mutation helpers ──────────────────────────────────────────────────────────

function useIntercompanyMutation(mutateFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: mutateFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['intercompany'] });
      qc.invalidateQueries({ queryKey: ['cash-flow'] });
      qc.invalidateQueries({ queryKey: ['upcoming-events'] });
    },
  });
}

// ── Period actions ────────────────────────────────────────────────────────────

export function useCrystallizePeriod() {
  return useIntercompanyMutation((yyyymm) =>
    api.post(`/intercompany/periods/${yyyymm}/crystallize`)
  );
}

export function useDecrystallizePeriod() {
  return useIntercompanyMutation((yyyymm) =>
    api.post(`/intercompany/periods/${yyyymm}/decrystallize`)
  );
}

// ── Line mutations ────────────────────────────────────────────────────────────

export function useAddServiceLine() {
  return useIntercompanyMutation(({ yyyymm, ...body }) =>
    api.post(`/intercompany/periods/${yyyymm}/lines`, body)
  );
}

export function useUpdateLine() {
  return useIntercompanyMutation(({ id, ...body }) =>
    api.patch(`/intercompany/lines/${id}`, body)
  );
}

export function useDeleteLine() {
  return useIntercompanyMutation((id) =>
    api.delete(`/intercompany/lines/${id}`)
  );
}
